import React, { useState, useEffect, useRef } from 'react';
import { Icon, Input } from 'antd';
import { JsonEditorWrapper, JsonEditorContainer } from './styles';
import { BUSINESS_CONTRACTS as trans } from './translationValues';
import VariableMentionPopup from '../EmailDesignPage/EmailBuilder/VariableMentionPopup';

const { TextArea } = Input;

// Entity.attribute pattern: word.word (e.g., InsuranceHolder.name)
const ENTITY_ATTRIBUTE_PATTERN = /[A-Za-z][A-Za-z0-9_]*\.[A-Za-z][A-Za-z0-9_]*/g;

const JsonMappingEditor = ({ value, onChange, t, globalVariables = [], updateGlobalVariables, relationships = [] }) => {
  const [jsonString, setJsonString] = useState('');
  const [showVariablePopup, setShowVariablePopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textAreaRef = useRef(null);
  const editorUuid = useRef(`json-editor-${Date.now()}`);
  const isUserEditingRef = useRef(false);
  const lastValidJsonStringRef = useRef('');
  const editingDebounceTimerRef = useRef(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (editingDebounceTimerRef.current) {
        clearTimeout(editingDebounceTimerRef.current);
      }
    };
  }, []);


  // Convert value to JSON string when it changes (only if not from user editing)
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:33',message:'useEffect value->jsonString ENTRY',data:{value:value,valueType:typeof value,valueKeys:value?Object.keys(value):null,currentJsonString:jsonString,isUserEditing:isUserEditingRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A,C'})}).catch(()=>{});
    // #endregion
    
    // Don't overwrite user input if they're actively editing
    if (isUserEditingRef.current) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:38',message:'useEffect skipping - user is editing',data:{currentJsonString:jsonString},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A,C'})}).catch(()=>{});
      // #endregion
      return;
    }
    
    if (value) {
      try {
        const stringified = JSON.stringify(value, null, 2);
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:45',message:'useEffect setting jsonString from value',data:{stringified:stringified,stringifiedLength:stringified.length,currentJsonString:jsonString},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A,C'})}).catch(()=>{});
        // #endregion
        setJsonString(stringified);
        lastValidJsonStringRef.current = stringified;
      } catch (err) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:52',message:'useEffect error stringifying value',data:{error:err.message,currentJsonString:jsonString},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A,C'})}).catch(()=>{});
        // #endregion
        setJsonString('');
      }
    } else {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:59',message:'useEffect value is falsy, setting to {}',data:{currentJsonString:jsonString},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      setJsonString('{}');
      lastValidJsonStringRef.current = '{}';
    }
  }, [value]);

  // Check if cursor is inside quotes
  const isInsideQuotes = (text, position) => {
    if (position === 0) return false;
    const beforeCursor = text.substring(0, position);
    let doubleQuotes = 0;
    let escaped = false;
    
    for (let i = 0; i < beforeCursor.length; i++) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (beforeCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (beforeCursor[i] === '"') {
        doubleQuotes++;
      }
    }
    return doubleQuotes % 2 === 1;
  };

  // Extract search term from text inside quotes
  const extractSearchTermFromQuotes = (text, cursorPos) => {
    if (!isInsideQuotes(text, cursorPos)) return '';
    
    const beforeCursor = text.substring(0, cursorPos);
    let lastQuoteIndex = -1;
    let escaped = false;
    
    // Find the opening quote before cursor
    for (let i = beforeCursor.length - 1; i >= 0; i--) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (beforeCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (beforeCursor[i] === '"') {
        lastQuoteIndex = i;
        break;
      }
    }
    
    if (lastQuoteIndex === -1) return '';
    
    // Extract text between opening quote and cursor
    return beforeCursor.substring(lastQuoteIndex + 1);
  };

  // Calculate popup position relative to textarea container
  const calculatePopupPosition = (textarea, cursorPos) => {
    if (!textarea) return null;
    
    const rect = textarea.getBoundingClientRect();
    const textBeforeCursor = jsonString.substring(0, cursorPos);
    const lines = textBeforeCursor.split('\n');
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 22.4; // 14px * 1.6
    const fontSize = parseFloat(computedStyle.fontSize) || 14;
    const charWidth = fontSize * 0.6; // Approximate monospace character width
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 12;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 12;
    
    // Calculate relative to textarea container
    const left = (lines[lines.length - 1].length * charWidth) + paddingLeft;
    const bottom = (lines.length * lineHeight) + paddingTop;
    
    return { left, bottom, target: textarea };
  };

  // Check if cursor is in a JSON value position (after colon, inside quotes)
  const isInValuePosition = (text, position) => {
    if (position === 0 || position >= text.length) return false;
    
    // Find the nearest colon before the cursor
    let colonIndex = -1;
    for (let i = position - 1; i >= 0; i--) {
      if (text[i] === ':') {
        colonIndex = i;
        break;
      }
      // Stop if we hit a newline or opening brace (start of new key)
      if (text[i] === '\n' || text[i] === '{') {
        break;
      }
    }
    
    // If no colon found, we're not in a value position
    if (colonIndex === -1) return false;
    
    // Get text after colon (don't trim - we need to check for quote with whitespace)
    const afterColon = text.substring(colonIndex + 1, position);
    // Check if there's a quote (allowing for whitespace before it)
    const trimmedAfterColon = afterColon.trim();
    const hasQuote = trimmedAfterColon.startsWith('"');
    
    // Check if cursor is inside quotes OR right at/after the opening quote
    const insideQuotes = isInsideQuotes(text, position);
    
    // Also check if cursor is exactly at the opening quote position
    const quoteIndex = afterColon.indexOf('"');
    const atOpeningQuote = quoteIndex !== -1 && (position === colonIndex + 1 + quoteIndex + 1);
    
    return hasQuote && (insideQuotes || atOpeningQuote);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    
    // Mark user as actively editing
    isUserEditingRef.current = true;
    
    // Clear existing debounce timer
    if (editingDebounceTimerRef.current) {
      clearTimeout(editingDebounceTimerRef.current);
    }
    
    // Set debounce timer to reset editing flag after user stops typing (500ms)
    editingDebounceTimerRef.current = setTimeout(() => {
      isUserEditingRef.current = false;
    }, 500);
    
    setJsonString(newValue);
    setCursorPosition(cursorPos);

    // Only call onChange when JSON is valid to prevent resetting parent state during typing
    if (onChange) {
      try {
        const parsed = JSON.parse(newValue);
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:166',message:'handleChange calling onChange with parsed JSON',data:{parsed:parsed,parsedKeys:Object.keys(parsed)},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A,B'})}).catch(()=>{});
        // #endregion
        onChange(parsed);
      } catch (e) {
        // Invalid JSON during typing - don't call onChange to avoid resetting parent state
        // The user is still typing, so we keep the local jsonString state but don't update parent
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/9d16fd3a-9022-4839-af3f-25b995c45adb',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'JsonMappingEditor.js:169',message:'handleChange JSON parse failed, NOT calling onChange',data:{parseError:e.message,newValue:newValue},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        // Do NOT call onChange({}) - this was causing the parent to reset value to {}, 
        // which triggered useEffect to reset jsonString, wiping user input
      }
    }

    // Auto-show popup when cursor is inside quotes in value position (after colon)
    const insideQuotes = isInsideQuotes(newValue, cursorPos);
    const inValuePosition = isInValuePosition(newValue, cursorPos);
    
    // Show popup if we're in a value position and either inside quotes or at the opening quote
    if (inValuePosition && textarea) {
      // Check if we just typed a quote (cursor is at or right after opening quote)
      const beforeCursor = newValue.substring(0, cursorPos);
      const lastChar = beforeCursor[beforeCursor.length - 1];
      const justTypedQuote = lastChar === '"';
      
      if (insideQuotes || justTypedQuote) {
        const searchTermText = extractSearchTermFromQuotes(newValue, cursorPos);
        const position = calculatePopupPosition(textarea, cursorPos);
        
        if (position) {
          setPopupPosition(position);
          setSearchTerm(searchTermText);
          setShowVariablePopup(true);
        }
      } else {
        setShowVariablePopup(false);
      }
    } else {
      setShowVariablePopup(false);
    }
  };

  const handleVariableSelect = (variable, allVariables) => {
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    if (!textarea || !variable) return;
    
    const placeholderText = variable.placeHolderText || variable.name;
    if (!placeholderText) return;
    
    // Find the opening quote before cursor and replace content between quotes
    const beforeCursor = jsonString.substring(0, cursorPosition);
    let lastQuoteIndex = -1;
    let escaped = false;
    
    // Find the opening quote before cursor
    for (let i = beforeCursor.length - 1; i >= 0; i--) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (beforeCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (beforeCursor[i] === '"') {
        lastQuoteIndex = i;
        break;
      }
    }
    
    if (lastQuoteIndex === -1) return;
    
    // Find the closing quote after cursor (or use cursor position if not found)
    const afterCursor = jsonString.substring(cursorPosition);
    let nextQuoteIndex = -1;
    escaped = false;
    
    for (let i = 0; i < afterCursor.length; i++) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (afterCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (afterCursor[i] === '"') {
        nextQuoteIndex = cursorPosition + i;
        break;
      }
    }
    
    // Replace content between quotes with placeholderText
    const before = jsonString.substring(0, lastQuoteIndex + 1);
    const after = nextQuoteIndex !== -1 ? jsonString.substring(nextQuoteIndex) : '"';
    const newJsonString = before + placeholderText + after;
    
    setJsonString(newJsonString);
    setShowVariablePopup(false);
    
    // Update cursor position after the closing quote
    setTimeout(() => {
      const newCursorPos = lastQuoteIndex + 1 + placeholderText.length + 1; // After closing quote
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
      setCursorPosition(newCursorPos);
    }, 0);
    
    // Update parent with parsed JSON if valid
    try {
      const parsed = JSON.parse(newJsonString);
      if (onChange) {
        onChange(parsed);
      }
    } catch (e) {
      // Invalid JSON - don't update parent, user can continue editing
    }
  };

  const handlePopupHide = () => {
    setShowVariablePopup(false);
  };

  // Validation is now only done on save, not on every text change

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <JsonEditorContainer style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <JsonEditorWrapper style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <TextArea
            ref={textAreaRef}
            value={jsonString}
            onChange={handleChange}
            data-uuid={editorUuid.current}
            style={{
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              lineHeight: '1.6',
              padding: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#fafafa',
              resize: 'none',
              flex: 1,
              minHeight: '400px',
              overflowY: 'auto',
              height: '100%'
            }}
            placeholder="{}"
          />
        </JsonEditorWrapper>
        {showVariablePopup && popupPosition && (
          <VariableMentionPopup
            uuid={editorUuid.current}
            searchTerm={searchTerm}
            onVariableSelect={handleVariableSelect}
            onHide={handlePopupHide}
            position={popupPosition}
            popupPlacement="bottom"
            target={popupPosition.target}
            globalVariables={globalVariables}
            updateGlobalVariables={updateGlobalVariables}
            relationships={relationships}
          />
        )}
      </JsonEditorContainer>
    </div>
  );
};

export default JsonMappingEditor;
